import { Component, Input, OnInit, inject } from "@angular/core";
import { Store } from "@ngrx/store";
import { MatDialog } from "@angular/material/dialog";
import { SystemSearchMetaRestService } from "@app/services/hal/system-search-meta-rest.service";
import { UntypedFormBuilder } from "@angular/forms";
import { Router } from "@angular/router";
import { NgxUiLoaderService } from "ngx-ui-loader";
import { ServiceErrorHandlerService } from "@app/view/shared/service-error-handler.service";
import { RowExpanderConfig } from "@bryntum/grid-thin";
import { BaseDataTablePageDirective } from "@app/view/shared/base-pages/base-data-table-page/base-data-table-page.directive";
import { ResourceFactMappingPattern } from "@app/models/rest/resource-fact-mapping-pattern";
import { MappingPatternStore } from "@app/services/store/mapping-pattern-store";
import { ConfirmDialogComponent } from "@app/view/shared/confirm-dialog/confirm-dialog.component";
import { MatToolbar } from "@angular/material/toolbar";
import { MatIconButton, MatButton } from "@angular/material/button";
import { MatIcon } from "@angular/material/icon";
import { GridComponent } from "../../../shared/base-pages/base-data-table-page/grid.component";
import { MatPaginator } from "@angular/material/paginator";

@Component({
  selector: "app-mapping-patterns",
  templateUrl: "./mapping-patterns.component.html",
  styleUrls: ["./mapping-patterns.component.scss"],
  imports: [
    MatToolbar,
    MatIconButton,
    MatIcon,
    MatButton,
    GridComponent,
    MatPaginator
  ]
})
export class MappingPatternsComponent
  extends BaseDataTablePageDirective<ResourceFactMappingPattern>
  implements OnInit
{
  mappingPatternStore: MappingPatternStore;
  protected store: Store<any>;
  protected dialog: MatDialog;
  protected formBuilder: UntypedFormBuilder;
  protected router = inject(Router);
  serverErrorHandler: ServiceErrorHandlerService;
  protected ngxUiLoaderService: NgxUiLoaderService;

  loading = false;
  public rowExpanderConfig: Partial<RowExpanderConfig> = {
    columnPosition: "last",
    renderer: async ({ record }) => {
      return `<div class="expander-container">
        <app-mapping-pattern-detail-component pattern="${record.id}" style="width:100%; display:block; height:350px;"></app-mapping-pattern-detail-component>
      </div>`;
    }
  };

  @Input()
  public columns: object[] = [
    {
      field: "description",
      text: "Description",
      flex: 1,
      editor: true
    },
    {
      field: "length",
      text: "Length (days)",
      width: 120,
      editor: true,
      type: "number"
    },
    {
      field: "patternOrigin",
      text: "Origin Date",
      width: 150,
      editor: true,
      type: "date",
      format: "YYYY-MM-DD"
    }
  ];

  constructor() {
    const systemSearchMetaRestService = inject(SystemSearchMetaRestService);
    const mappingPatternStore = inject(MappingPatternStore);
    const store = inject<Store<any>>(Store);
    const dialog = inject(MatDialog);
    const formBuilder = inject(UntypedFormBuilder);
    const serverErrorHandler = inject(ServiceErrorHandlerService);
    const ngxUiLoaderService = inject(NgxUiLoaderService);

    super(
      systemSearchMetaRestService,
      mappingPatternStore,
      store,
      dialog,
      "MappingPattern",
      formBuilder,
      serverErrorHandler,
      ngxUiLoaderService
    );

    this.mappingPatternStore = mappingPatternStore;
    this.store = store;
    this.dialog = dialog;
    this.formBuilder = formBuilder;
    this.serverErrorHandler = serverErrorHandler;
    this.ngxUiLoaderService = ngxUiLoaderService;
  }

  public get pristine() {
    return (
      !this.gridStore.changes ||
      (this.gridStore.changes.added.length === 0 &&
        this.gridStore.changes.removed.length === 0 &&
        this.gridStore.changes.modified.length === 0)
    );
  }

  ngOnInit() {
    super.ngOnInit();
    this.gridStore.autoCommit = false;
    this.gridStore.load({});
  }

  addRow() {
    this.gridStore.insert(0, {
      description: "",
      length: 7,
      patternOrigin: new Date()
    });
  }

  commit() {
    const result = this.gridStore.commit();
    if (result instanceof Promise) {
      result
        .then(() => {
          console.log("Successfully committed changes");
          this.ngOnInit();
        })
        .catch((error) => this.serverErrorHandler.handleError(error));
    }
  }

  onEvent(event: any) {
    super.onEvent(event);

    if (event.type === "cellDblClick") {
      // Handle cell double click if needed
      console.log("Cell double clicked:", event);
    }
  }

  showHelp() {
    this.dialog.open(ConfirmDialogComponent, {
      data: {
        title: "Mapping Patterns Help",
        message:
          "Mapping patterns are used for controlling skill and zone assignments to resources. These patterns help establish recurring schedules for your resources.  For instance, you could have operatives who work one week in one zone, and another week in another zone.  Or have operatives who work a different skill each day of the week.",
        messages: [
          "To create a weekly repeating pattern, typically you would set any Monday as the origin date and use 7 days for the length. However, you can use any length or origin date to create custom patterns that fit your specific scheduling needs.",
          "Add time blocks within the pattern to specify which skills and zones operatives should have on particular dates. Each time block can be configured with precise hours and minutes for flexible scheduling.",
          "Important: If a mapping pattern is set on a skill or zone mapping, only shifts that start within the time blocks of the pattern will have those skills/zones.  If a shift starts outside of the time blocks, the skill/zone will not be applied."
        ],
        showCancelButton: false
      },
      width: "500px"
    });
  }
}
